import React from 'react';
import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import { WifiNetworkListItem } from './WifiNetworkListItem'; // Adjust path as needed

export type WifiNetwork = {
  ssid: string;
  signal: number; // Assumes RSSI format from local device (e.g., -45 dBm)
  security: string[];
};

type Props = {
  wifiNetworks: WifiNetwork[];
  selectedIndex: number | null;
  onClick: (index: number | null) => void;
  isLoading: boolean;
  isDisabled?: boolean;
};

/**
 * WifiNetworksList - Reusable list renderer for wifi networks.
 */
export function WifiNetworkList({ wifiNetworks, selectedIndex, onClick, isLoading, isDisabled }: Props) {
  const { t } = useTranslation();
  const bgColor = useColorModeValue('customGrey.200', 'customGrey.800');
  const shadowColor = useColorModeValue('rgba(237,238,238,1)', 'rgba(39,40,42,1)');

  return (
    <Box position="relative" w="100%" h="50vh" bg={bgColor} borderRadius={6} data-testid="wifi-networks-container">
      {isLoading ? (
        <CenteredLoader text={`${t('Common.loading')}...`} />
      ) : (
        <Box overflowY="auto" h="100%" pr={2}>
          {!wifiNetworks.length ? (
            <Flex h="100%" align="center" justify="center">
              <Text data-testid="no-wifi-networks-found">{t('Set Up Hardware.no wifi networks found')}</Text>
            </Flex>
          ) : (
            wifiNetworks.map(({ ssid, signal }, index) => (
              <WifiNetworkListItem
                key={`${ssid}-${index}`}
                ssid={ssid}
                signalStrength={signal}
                isSelected={selectedIndex === index}
                isDisabled={isDisabled}
                onClick={() => onClick(index)}
                data-testid={`wifi-network-${index}`}
              />
            ))
          )}
        </Box>
      )}

      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        height="20px"
        pointerEvents="none"
        bg={`linear-gradient(to top, ${shadowColor}, transparent)`}
        borderBottomRadius={6}
      />
    </Box>
  );
}
